// Payslips → numbers an advisor can use (0.7). Reads the text lines of a payslip (from @vero/pdf, cells separated by
// TAB) in Greek or English, finds the employee's earnings and deductions, checks that they add up, and turns one or
// more months into an annual picture for the tax estimate (tax.ts).
//
// Rules:
//  - nothing is invented: a value that is not on the payslip stays null and is shown as "not found";
//  - employer contributions (social insurance, cohesion fund, GHS, redundancy, holiday fund…) are never counted;
//  - identity numbers (social insurance no., tax id, IBAN) are not read at all;
//  - every value keeps the line it came from, so the advisor can check it before anything is saved;
//  - the text is untrusted input: it is only matched against fixed patterns.
import { searchKey } from './rules.ts';
import { parseAmount } from './extraction.ts';
import type { TaxInput, TaxRuleset } from './tax.ts';

export const PAYSLIP_KEYS = ['gross', 'basic', 'allowances', 'thirteenth', 'si', 'ghs', 'provident', 'pension', 'medical', 'union', 'tax', 'otherDeductions', 'deductionsTotal', 'net'] as const;
export type PayslipKey = typeof PAYSLIP_KEYS[number];
/** the employee deductions that reduce net pay */
export const PAYSLIP_DEDUCTIONS: PayslipKey[] = ['si', 'ghs', 'provident', 'pension', 'medical', 'union', 'tax', 'otherDeductions'];

export interface PayslipField {
  key: PayslipKey; value: number | null; ytd?: number | null; confidence: number; page: number; snippet: string;
  /** computed from other lines (e.g. gross = basic + allowances), not read from a single line */
  derived?: boolean;
  alternatives?: number[];
}
export interface Payslip {
  employer: string | null; employee: string | null;
  period: { month: number | null; year: number | null; text: string | null };
  currency: string;
  fields: Partial<Record<PayslipKey, PayslipField>>;
  /** the payslip has a year-to-date column */
  ytdColumn: boolean;
  /** a 13th salary is paid in this payslip or mentioned on it */
  thirteenthMentioned: boolean;
  checks: { netMatches: boolean | null; difference: number | null; unidentifiedDeductions: number; siRate: number | null; ghsRate: number | null };
  /** i18n keys (payslip.warn.*) */
  warnings: string[];
  /** characters of text read (0: scanned image, enter the amounts by hand) */
  chars: number;
}

// --- recognising labels ------------------------------------------------------------------------------------
const norm = (s: string) => searchKey(s).replace(/[.:€%()\[\]\-–—·•*_/|,;]/g, ' ').replace(/\b\d+(?:[.,]\d+)?\b/g, ' ').replace(/\s+/g, ' ').trim();
const EMPLOYER_MARK = /εργοδοτ|employer|company contribution|συνοχ|cohesion|πλεονα|redundancy|βιομηχανικ|industrial training|ταμειο αδειων|holiday fund|ανεργ|unemployment/;
type Rule = [PayslipKey | 'ignore', RegExp];
// order matters: totals before their parts, specific before generic
const RULES: Rule[] = [
  ['ignore', /αριθμ|number|\bcode\b|ποσοστ|\brate\b|ωρες|hours|ημερες|days|τιμη ωρας|hourly/],
  ['deductionsTotal', /συνολο κρατησεων|συνολικες κρατησεις|συνολο αφαιρεσεων|total deductions|deductions total|total of deductions/],
  ['net', /καθαρο πληρωτεο|καθαρες αποδοχες|καθαρο ποσο|καθαρος μισθος|πληρωτεο|καθαρα|net pay|net salary|net amount|net to pay|take home|amount paid|^net$/],
  ['gross', /μικτες αποδοχες|μικτος μισθος|μικτες|μικτα|συνολο αποδοχων|συνολικες αποδοχες|ακαθαριστ|gross pay|gross salary|gross earnings|total earnings|total gross|total pay|^gross$|gross/],
  ['thirteenth', /13ος|13ου|δεκατος τριτος|δεκατου τριτου|13th/],
  ['basic', /βασικος μισθος|βασικος|basic salary|basic pay|^salary$|^μισθος$/],
  ['si', /κοινωνικες ασφαλισεις|κοινωνικων ασφαλισεων|κοιν ασφαλισεις|κοιν ασφ|social insurance|social ins|^si$|^κ α$/],
  ['ghs', /γεσυ|γ ε σ υ|γενικο συστημα υγειας|εισφορα υγειας|\bghs\b|gesy|nhis|national health|general healthcare/],
  ['provident', /ταμειο προνοιας|ταμειου προνοιας|προνοια|provident/],
  ['pension', /ταμειο συνταξεων|συνταξιοδοτικ|pension/],
  ['medical', /ιατροφαρμακευτικ|ταμειο υγειας|medical/],
  ['union', /συντεχνι|union|επαγγελματικ συνδεσμ|professional body/],
  ['tax', /φορος εισοδηματος|παρακρατηση φορου|παρακρατηθεις φορος|^φορος$|φορος|income tax|paye|withholding tax|^tax$/],
  ['allowances', /επιδομα|allowance|υπερωρ|overtime|bonus|μπονους|προμηθει|commission|αποζημιωσ|benefit in kind/],
  ['otherDeductions', /δανει|loan|προκαταβολ|advance|κρατηση|deduction/],
];
function classify(label: string): PayslipKey | 'employer' | null {
  const n = norm(label);
  if (!n) return null;
  if (EMPLOYER_MARK.test(n)) return 'employer';
  if (/\b13\s*(?:ος|ου|th)|δεκατ\S* τριτ/.test(searchKey(label))) return 'thirteenth';
  for (const [key, re] of RULES) if (re.test(n)) return key === 'ignore' ? null : key;
  return null;
}

// --- amounts -----------------------------------------------------------------------------------------------
// money on payslips always has two decimals; percentages, dates and identity numbers do not match
const AMOUNT = /(?<![\d.,'])-?(?:\d{1,3}(?:[.,  ']\d{3})+|\d+)[.,]\d{2}(?![\d%])/g;
const AMOUNT_ONE = new RegExp(AMOUNT.source);
interface Pair { label: string; amounts: number[]; line: string; page: number }
function pairsOf(line: string, page: number): { pairs: Pair[]; text: string } {
  const pairs: Pair[] = [];
  let last = 0;
  let label = '';
  let current: Pair | null = null;
  for (const m of line.matchAll(AMOUNT)) {
    const between = line.slice(last, m.index).replace(/\t/g, ' ');
    // a text segment is a new label only if it has letters (currency signs and "8,80%" are not labels)
    if ((between.replace(/€|eur\b|ευρω/gi, '').match(/\p{L}/gu) ?? []).length >= 2) { label = between.trim(); current = null; }
    const v = parseAmount(m[0].replace(/[  ']/g, ''));
    if (v !== null) {
      if (!current) { current = { label, amounts: [], line, page }; pairs.push(current); }
      current.amounts.push(v);
    }
    last = (m.index ?? 0) + m[0].length;
  }
  return { pairs, text: line.slice(last) };
}

// --- header fields -----------------------------------------------------------------------------------------
const MONTHS: [RegExp, number][] = [
  [/^ιαν|^jan/, 1], [/^φεβ|^feb/, 2], [/^μαρ|^mar/, 3], [/^απρ|^apr/, 4], [/^μαι|^may/, 5], [/^ιουν|^jun/, 6],
  [/^ιουλ|^jul/, 7], [/^αυγ|^aug/, 8], [/^σεπ|^sep/, 9], [/^οκτ|^oct/, 10], [/^νοε|^nov/, 11], [/^δεκ|^dec/, 12],
];
function monthOf(word: string): number | null { const w = searchKey(word); for (const [re, n] of MONTHS) if (re.test(w)) return n; return null; }
function parsePeriod(text: string): { month: number | null; year: number | null } {
  const t = text.trim();
  let m = /\b(\d{1,2})\s*[/.\-]\s*(20\d{2})\b/.exec(t);
  if (m) return { month: Number(m[1]) >= 1 && Number(m[1]) <= 12 ? Number(m[1]) : null, year: Number(m[2]) };
  m = /\b(20\d{2})\s*[/.\-]\s*(\d{1,2})\b/.exec(t);
  if (m) return { month: Number(m[2]) >= 1 && Number(m[2]) <= 12 ? Number(m[2]) : null, year: Number(m[1]) };
  m = /(\p{L}{3,})\.?\s+(20\d{2})/u.exec(t);
  if (m && monthOf(m[1]!)) return { month: monthOf(m[1]!), year: Number(m[2]) };
  const word = /(\p{L}{3,})/u.exec(t);
  const y = /\b(20\d{2})\b/.exec(t);
  return { month: word ? monthOf(word[1]!) : null, year: y ? Number(y[1]) : null };
}
const HEADER: [keyof Pick<Payslip, 'employer' | 'employee'> | 'period', RegExp][] = [
  ['employer', /^(?:εργοδοτης|εργοδοτρια εταιρεια|εταιρεια|employer|company)$/],
  ['employee', /^(?:υπαλληλος|ονομα υπαλληλου|ονοματεπωνυμο|εργαζομενος|ονομα|employee|employee name|name)$/],
  ['period', /^(?:περιοδος|περιοδος μισθοδοσιας|μηνας|pay period|period|month|payroll period)$/],
];

/** Read one payslip. `pages`: lines per page; cells within a line separated by TAB. */
export function parsePayslip(pages: string[][]): Payslip {
  const out: Payslip = { employer: null, employee: null, period: { month: null, year: null, text: null }, currency: 'EUR', fields: {}, ytdColumn: false, thirteenthMentioned: false, checks: { netMatches: null, difference: null, unidentifiedDeductions: 0, siRate: null, ghsRate: null }, warnings: [], chars: 0 };
  const all = pages.flat().join('\n');
  out.chars = all.replace(/\s/g, '').length;
  if (/£|gbp/i.test(all) && !/€|eur/i.test(all)) out.currency = 'GBP';
  out.thirteenthMentioned = /13\s*ος\s*μισθ|13ου μισθ|δέκατ\S* τρίτ|13th (?:salary|month)/i.test(all);
  const cands = new Map<PayslipKey, { value: number; ytd: number | null; page: number; line: string; strong: boolean }[]>();
  let section: 'employer' | null = null;
  pages.forEach((lines, pi) => {
    for (const raw of lines) {
      const line = raw.trim();
      if (!line || line.length > 400) continue;
      // header "Label: value" cells (employer, employee, period) — never identity numbers
      for (const cell of line.split('\t')) {
        const hm = /^([^:]{2,40}):\s*(.+)$/.exec(cell.trim());
        if (!hm) continue;
        const key = norm(hm[1]!);
        for (const [field, re] of HEADER) {
          if (!re.test(key)) continue;
          const val = hm[2]!.trim().slice(0, 120);
          if (field === 'period') { if (!out.period.text) { out.period = { ...parsePeriod(val), text: val }; } }
          else if (!out[field] && /\p{L}/u.test(val) && !AMOUNT_ONE.test(val)) out[field] = val;
        }
      }
      const { pairs } = pairsOf(line, pi + 1);
      if (!pairs.length) {
        // section headers and column headers
        const n = norm(line);
        if (/year to date|\bytd\b|απο αρχη|απο την αρχη|σωρευτικ|προοδευτικ|συνολο ετους|cumulative|to date/.test(n)) out.ytdColumn = true;
        if (n) section = EMPLOYER_MARK.test(n) ? 'employer' : null;
        continue;
      }
      let lineEmployer = section === 'employer';
      for (const p of pairs) {
        const kind = classify(p.label);
        if (kind === 'employer') { lineEmployer = true; continue; }
        if (!kind) continue;
        if (lineEmployer && kind !== 'net' && kind !== 'gross') continue;
        const list = cands.get(kind) ?? [];
        const strong = kind === 'gross' && /συνολο|συνολικ|μικτες αποδοχες|gross pay|total/.test(norm(p.label));
        list.push({ value: p.amounts[0]!, ytd: out.ytdColumn && p.amounts.length > 1 ? p.amounts[1]! : null, page: p.page, line: p.line, strong });
        cands.set(kind, list);
      }
    }
  });
  // choose one value per key
  for (const [key, list] of cands) {
    let pick = list[0]!;
    if (key === 'gross') pick = list.find((c) => c.strong) ?? list.reduce((a, b) => (b.value > a.value ? b : a));
    if (key === 'net' || key === 'deductionsTotal') pick = list[list.length - 1]!;
    if (key === 'allowances' || key === 'otherDeductions') {
      // several allowance / other-deduction lines add up
      const sum = Math.round(list.reduce((s, c) => s + c.value, 0) * 100) / 100;
      const ytd = list.every((c) => c.ytd != null) ? Math.round(list.reduce((s, c) => s + (c.ytd ?? 0), 0) * 100) / 100 : null;
      out.fields[key] = { key, value: sum, ytd, confidence: 0.85, page: list[0]!.page, snippet: list.map((c) => c.line).join(' | ').slice(0, 200) };
      continue;
    }
    const alts = [...new Set(list.map((c) => c.value).filter((v) => v !== pick.value))];
    out.fields[key] = { key, value: pick.value, ytd: pick.ytd, confidence: alts.length ? 0.55 : 0.9, page: pick.page, snippet: pick.line.replace(/\t/g, '  ').slice(0, 160), ...(alts.length ? { alternatives: alts } : {}) };
  }
  if (out.fields.thirteenth?.value) out.thirteenthMentioned = true;
  // gross from its parts when the total is not printed
  if (!out.fields.gross && (out.fields.basic || out.fields.allowances || out.fields.thirteenth)) {
    const g = (out.fields.basic?.value ?? 0) + (out.fields.allowances?.value ?? 0) + (out.fields.thirteenth?.value ?? 0);
    out.fields.gross = { key: 'gross', value: Math.round(g * 100) / 100, confidence: 0.7, page: out.fields.basic?.page ?? 1, snippet: 'basic + allowances', derived: true };
  }
  // checks: gross − deductions = net
  const v = (k: PayslipKey) => out.fields[k]?.value ?? 0;
  const gross = out.fields.gross?.value ?? null;
  const known = Math.round(PAYSLIP_DEDUCTIONS.reduce((s, k) => s + v(k), 0) * 100) / 100;
  if (out.fields.deductionsTotal?.value != null && out.fields.deductionsTotal.value - known > 0.5) {
    out.checks.unidentifiedDeductions = Math.round((out.fields.deductionsTotal.value - known) * 100) / 100;
    out.warnings.push('payslip.warn.unidentified_deductions');
  }
  if (gross != null && out.fields.net?.value != null) {
    const diff = Math.round((out.fields.net.value - (gross - known - out.checks.unidentifiedDeductions)) * 100) / 100;
    out.checks.difference = diff;
    out.checks.netMatches = Math.abs(diff) <= 1;
    if (!out.checks.netMatches) out.warnings.push('payslip.warn.net_mismatch');
    else for (const k of PAYSLIP_KEYS) { const f = out.fields[k]; if (f && !f.alternatives) f.confidence = Math.max(f.confidence, 0.95); }
  }
  if (gross) {
    if (out.fields.si?.value) out.checks.siRate = Math.round((out.fields.si.value / gross) * 10000) / 10000;
    if (out.fields.ghs?.value) out.checks.ghsRate = Math.round((out.fields.ghs.value / gross) * 10000) / 10000;
  }
  if (!out.chars) out.warnings.push('payslip.warn.no_text');
  else if (gross == null) out.warnings.push('payslip.warn.no_gross');
  if (out.chars && !out.fields.si) out.warnings.push('payslip.warn.no_si');
  if (!out.period.year) out.warnings.push('payslip.warn.no_period');
  return out;
}

// --- from months to a year ---------------------------------------------------------------------------------
export interface PayslipMonth { gross: number; si: number; ghs: number; provident: number; pension: number; medical: number; union: number; tax: number; other: number; net: number | null; thirteenth: number }
export function payslipMonth(p: Payslip): PayslipMonth {
  const f = (k: PayslipKey) => p.fields[k]?.value ?? 0;
  return { gross: f('gross'), si: f('si'), ghs: f('ghs'), provident: f('provident'), pension: f('pension'), medical: f('medical'), union: f('union'), tax: f('tax'), other: f('otherDeductions') + p.checks.unidentifiedDeductions, net: p.fields.net?.value ?? null, thirteenth: f('thirteenth') };
}
const r2 = (n: number) => Math.round(n * 100) / 100;
/**
 * A typical month from one or more payslips. A payslip that contains a 13th salary is scaled back to a regular month
 * (its contributions in proportion) so the 13th is counted once, through `salariesPerYear`.
 */
export function typicalMonth(months: PayslipMonth[]): PayslipMonth {
  if (!months.length) return { gross: 0, si: 0, ghs: 0, provident: 0, pension: 0, medical: 0, union: 0, tax: 0, other: 0, net: null, thirteenth: 0 };
  const regular = months.map((m) => {
    if (!m.thirteenth || m.gross <= m.thirteenth) return m;
    const k = (m.gross - m.thirteenth) / m.gross;
    return { ...m, gross: m.gross - m.thirteenth, si: m.si * k, ghs: m.ghs * k, provident: m.provident * k, pension: m.pension * k, tax: m.tax * k, net: m.net == null ? null : m.net * k, thirteenth: 0 };
  });
  const avg = (k: keyof PayslipMonth) => r2(regular.reduce((s, m) => s + Number(m[k] ?? 0), 0) / regular.length);
  return { gross: avg('gross'), si: avg('si'), ghs: avg('ghs'), provident: avg('provident'), pension: avg('pension'), medical: avg('medical'), union: avg('union'), tax: avg('tax'), other: avg('other'), net: regular.every((m) => m.net != null) ? avg('net') : null, thirteenth: 0 };
}
export interface AnnualFromPayslips {
  salariesPerYear: number;
  gross: number; si: number; ghs: number; provident: number; pension: number; medical: number; union: number; taxWithheld: number; net: number | null;
}
/** Annual figures: a typical month × salaries per year (12, 13 or 14); social insurance within the insurable ceiling. */
export function annualFromPayslips(month: PayslipMonth, salariesPerYear: number, rs?: TaxRuleset | null): AnnualFromPayslips {
  const n = [12, 13, 14].includes(salariesPerYear) ? salariesPerYear : 12;
  const gross = r2(month.gross * n);
  let si = r2(month.si * n);
  const ceiling = rs?.params.socialInsuranceCeiling, rate = rs?.params.socialInsuranceRate;
  if (ceiling && rate) si = Math.min(si, r2(ceiling * rate));
  return { salariesPerYear: n, gross, si, ghs: r2(month.ghs * n), provident: r2(month.provident * n), pension: r2(month.pension * n), medical: r2(month.medical * n), union: r2(month.union * n), taxWithheld: r2(month.tax * n), net: month.net == null ? null : r2(month.net * n) };
}
/** The tax-estimate input for these annual figures (actual contributions, not estimated ones). */
export function payslipTaxInput(a: AnnualFromPayslips, extra: Partial<Pick<TaxInput, 'lifePolicies' | 'children' | 'householdIncome' | 'singleParent' | 'housingInterestOrRent' | 'single'>> = {}): TaxInput {
  return {
    grossIncome: a.gross, socialInsurance: a.si, ghs: a.ghs, providentFund: a.provident, pensionFund: a.pension, medicalFund: a.medical,
    employmentExpenses: a.union, ...extra,
  };
}
